import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search, CornerDownLeft, Home, Users, UserCheck, CheckSquare, CalendarDays,
  Calendar, FileText, TrendingUp, Building2, Receipt, BookOpen, Bot, Server,
  CreditCard, Shield, ArrowRight,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import api from '../api';

// Destinazioni di navigazione (filtrate per ruolo)
const NAV = [
  { label: 'Home',            to: '/',               icon: Home },
  { label: 'Il mio lavoro (bacheca)', to: '/studio', icon: CheckSquare },
  { label: 'Panoramica',      to: '/panoramica',     icon: Home },
  { label: 'Cockpit agenzia', to: '/cockpit',        icon: TrendingUp, admin: true },
  { label: 'La mia giornata', to: '/oggi',           icon: CalendarDays },
  { label: 'Clienti attivi',  to: '/clienti-attivi', icon: UserCheck },
  { label: 'Tutti i clienti', to: '/clienti',        icon: Users },
  { label: 'Leads',           to: '/leads',          icon: Users },
  { label: 'Task',            to: '/task',            icon: CheckSquare },
  { label: 'Scadenzario',     to: '/scadenzario',    icon: CalendarDays },
  { label: 'Calendario',      to: '/calendario',     icon: Calendar },
  { label: 'Piano editoriale',to: '/ped',            icon: FileText },
  { label: 'Produzione video', to: '/produzione',    icon: Calendar },
  { label: 'Storie',          to: '/ped-storie',     icon: FileText },
  { label: 'Guida social',    to: '/guida',          icon: FileText },
  { label: 'Trend',           to: '/trend',          icon: FileText },
  { label: 'Guida ai trend',  to: '/guida-trend',    icon: FileText },
  { label: 'Client Vault',    to: '/client-vault',   icon: FileText },
  { label: 'Pubblicazioni',   to: '/pubblicazioni',  icon: FileText },
  { label: 'Marketing Analytics', to: '/analytics',  icon: TrendingUp, social: true },
  { label: 'Cosa funziona',   to: '/cosa-funziona', icon: TrendingUp, social: true },
  { label: 'Vista Agenzia',   to: '/agenzia',        icon: Building2, admin: true },
  { label: 'Fatturazione',    to: '/fatturazione',   icon: Receipt, admin: true },
  { label: 'Team',            to: '/team',            icon: Users },
  { label: 'Schede Pep',      to: '/pep-schede',     icon: BookOpen },
  { label: 'Eden HQ',         to: '/eden-hq',        icon: Building2, admin: true },
  { label: 'Agenti dal vivo', to: '/agenti-vivi',    icon: Bot },
  { label: 'Server',          to: '/servers',         icon: Server, admin: true },
  { label: 'Abbonamenti',     to: '/abbonamenti',    icon: CreditCard, admin: true },
  { label: 'Admin',           to: '/admin',           icon: Shield, admin: true },
];

const norm = (s) => (s || '').toLowerCase();

export default function CommandPalette() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [sel, setSel] = useState(0);
  const [clienti, setClienti] = useState([]);
  const [task, setTask] = useState([]);
  const [contenuti, setContenuti] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const inputRef = useRef(null);

  const isAdmin = user?.ruolo === 'admin';
  const isSocial = ['admin', 'boss', 'social_manager'].includes(user?.ruolo);

  // Apertura con Cmd/Ctrl+K
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen(o => !o);
      } else if (e.key === 'Escape') {
        setOpen(false);
      }
    };
    const onOpen = () => setOpen(true);
    window.addEventListener('keydown', onKey);
    window.addEventListener('eden-open-palette', onOpen);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('eden-open-palette', onOpen);
    };
  }, []);

  // Carica dati ricercabili alla prima apertura
  useEffect(() => {
    if (open && !loaded) {
      Promise.all([
        api.get('/clienti', { params: { limit: 300 }, silent: true }).catch(() => ({ data: [] })),
        api.get('/task', { silent: true }).catch(() => ({ data: [] })),
        api.get('/pubblicazioni', { params: { limit: 500 }, silent: true }).catch(() => ({ data: [] })),
      ]).then(([c, t, p]) => {
        setClienti(Array.isArray(c.data) ? c.data : []);
        setTask(Array.isArray(t.data) ? t.data : []);
        setContenuti(Array.isArray(p.data) ? p.data : []);
        setLoaded(true);
      });
    }
  }, [open, loaded]);

  // Focus input all'apertura, reset alla chiusura
  useEffect(() => {
    if (open) { setQ(''); setSel(0); setTimeout(() => inputRef.current?.focus(), 30); }
  }, [open]);

  const results = useMemo(() => {
    const query = norm(q);
    const navItems = NAV
      .filter(n => (!n.admin || isAdmin) && (!n.social || isSocial))
      .filter(n => !query || norm(n.label).includes(query))
      .map(n => ({ type: 'nav', label: n.label, icon: n.icon, action: () => navigate(n.to) }));

    let clientiItems = [], taskItems = [], contenutiItems = [];
    if (query) {
      clientiItems = clienti
        .filter(c => norm(c.azienda).includes(query) || norm(c.nome).includes(query))
        .slice(0, 6)
        .map(c => ({
          type: 'cliente', label: c.azienda || c.nome,
          sub: c.azienda && c.nome ? c.nome : (c.settore || 'Cliente'),
          icon: Users, action: () => navigate(`/clienti/${c.id}`),
        }));
      taskItems = task
        .filter(t => norm(t.titolo).includes(query))
        .slice(0, 6)
        .map(t => ({
          type: 'task', label: t.titolo,
          sub: t.cliente_nome || t.stato?.replace('_', ' '),
          icon: CheckSquare, action: () => navigate(`/task?open=${t.id}`),
        }));
      contenutiItems = contenuti
        .filter(p => norm(p.titolo_post).includes(query) || norm(p.id_post).includes(query) || norm(p.cliente).includes(query))
        .slice(0, 6)
        .map(p => ({
          type: 'contenuto', label: `${p.id_post ? p.id_post + ' · ' : ''}${p.titolo_post}`,
          sub: `${p.cliente || ''}${p.formato ? ' · ' + p.formato : ''}`,
          icon: FileText, action: () => navigate(`/ped/contenuto/${p.id}`),
        }));
    }

    const groups = [];
    if (navItems.length) groups.push({ heading: 'Vai a', items: navItems });
    if (clientiItems.length) groups.push({ heading: 'Clienti', items: clientiItems });
    if (contenutiItems.length) groups.push({ heading: 'Contenuti', items: contenutiItems });
    if (taskItems.length) groups.push({ heading: 'Task', items: taskItems });
    return groups;
  }, [q, clienti, task, contenuti, isAdmin, isSocial, navigate]);

  const flat = useMemo(() => results.flatMap(g => g.items), [results]);

  useEffect(() => { setSel(0); }, [q]);

  const choose = useCallback((item) => {
    if (!item) return;
    item.action();
    setOpen(false);
  }, []);

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setSel(s => Math.min(s + 1, flat.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setSel(s => Math.max(s - 1, 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); choose(flat[sel]); }
  };

  if (!open) return null;

  let runningIndex = -1;
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 800, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', paddingTop: '12vh', paddingLeft: 16, paddingRight: 16 }}>
      <div onClick={() => setOpen(false)} style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(3px)' }} />
      <div className="eden-toast-in" style={{
        position: 'relative', zIndex: 1, width: 'min(560px, 100%)', maxHeight: '70vh',
        background: 'var(--bg2, #14141c)', border: '1px solid var(--border)', borderRadius: 14,
        boxShadow: '0 20px 60px rgba(0,0,0,0.55)', display: 'flex', flexDirection: 'column', overflow: 'hidden',
      }}>
        {/* Input */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '14px 16px', borderBottom: '1px solid var(--border)' }}>
          <Search size={17} color="var(--text-dim)" style={{ flexShrink: 0 }} />
          <input ref={inputRef} value={q} onChange={e => setQ(e.target.value)} onKeyDown={onKeyDown}
            placeholder="Cerca clienti, task o vai a una pagina…"
            style={{ flex: 1, background: 'none', border: 'none', outline: 'none', color: 'var(--text)', fontSize: 15, fontFamily: 'inherit' }} />
          <kbd style={{ fontSize: 10.5, color: 'var(--text-dim)', border: '1px solid var(--border)', borderRadius: 5, padding: '2px 6px', flexShrink: 0 }}>ESC</kbd>
        </div>

        {/* Results */}
        <div style={{ overflowY: 'auto', padding: '6px' }}>
          {flat.length === 0 ? (
            <div style={{ padding: '32px 16px', textAlign: 'center', color: 'var(--text-dim)', fontSize: 13 }}>
              {loaded ? 'Nessun risultato' : 'Caricamento…'}
            </div>
          ) : results.map(group => (
            <div key={group.heading} style={{ marginBottom: 4 }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: '0.06em', padding: '8px 10px 4px' }}>
                {group.heading}
              </div>
              {group.items.map(item => {
                runningIndex++;
                const idx = runningIndex;
                const active = idx === sel;
                const Icon = item.icon;
                return (
                  <div key={`${item.type}-${item.label}-${idx}`}
                    onMouseEnter={() => setSel(idx)}
                    onClick={() => choose(item)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 11, padding: '9px 10px', borderRadius: 8, cursor: 'pointer',
                      background: active ? 'var(--cyan-dim)' : 'transparent',
                    }}>
                    <Icon size={15} color={active ? 'var(--cyan)' : 'var(--text-muted)'} style={{ flexShrink: 0 }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13.5, color: active ? 'var(--cyan)' : 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.label}</div>
                      {item.sub && <div style={{ fontSize: 11, color: 'var(--text-dim)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.sub}</div>}
                    </div>
                    {active && <CornerDownLeft size={13} color="var(--text-dim)" style={{ flexShrink: 0 }} />}
                  </div>
                );
              })}
            </div>
          ))}
        </div>

        {/* Footer hint */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '9px 14px', borderTop: '1px solid var(--border)', fontSize: 10.5, color: 'var(--text-dim)' }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><ArrowRight size={11} style={{ transform: 'rotate(90deg)' }} /> naviga</span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><CornerDownLeft size={11} /> apri</span>
          <span style={{ marginLeft: 'auto' }}>⌘K / Ctrl+K per aprire</span>
        </div>
      </div>
    </div>
  );
}
